import type { Room } from 'livekit-client'
import { RoomEvent } from 'livekit-client'
import { useEffect, useState } from 'react'

type QuestionStartedEvent = {
  readonly type: 'question_started'
  readonly index: number
  readonly total: number
  readonly text: string
}

type QuestionCompletedEvent = {
  readonly type: 'question_completed'
  readonly index: number
}

type InterviewProgressEvent = QuestionStartedEvent | QuestionCompletedEvent

export type InterviewProgressState = {
  readonly currentQuestion: {
    readonly index: number
    readonly total: number
    readonly text: string
  } | null
  readonly completedCount: number
}

export const parseInterviewProgressEvent = (
  data: Uint8Array,
): InterviewProgressEvent | null => {
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(data))
    if (typeof parsed !== 'object' || parsed === null) return null

    const event = parsed as Record<string, unknown>

    if (
      event['type'] === 'question_started' &&
      typeof event['index'] === 'number' &&
      typeof event['total'] === 'number' &&
      typeof event['text'] === 'string'
    ) {
      return {
        type: 'question_started',
        index: event['index'],
        total: event['total'],
        text: event['text'],
      }
    }

    if (
      event['type'] === 'question_completed' &&
      typeof event['index'] === 'number'
    ) {
      return { type: 'question_completed', index: event['index'] }
    }

    return null
  } catch {
    return null
  }
}

export const useInterviewProgress = (room: Room): InterviewProgressState => {
  const [state, setState] = useState<InterviewProgressState>({
    currentQuestion: null,
    completedCount: 0,
  })

  useEffect(() => {
    const handleDataReceived = (data: Uint8Array) => {
      const event = parseInterviewProgressEvent(data)
      if (!event) return

      switch (event.type) {
        case 'question_started':
          setState((prev) => ({
            ...prev,
            currentQuestion: {
              index: event.index,
              total: event.total,
              text: event.text,
            },
          }))
          break
        case 'question_completed':
          setState((prev) => ({
            ...prev,
            completedCount: prev.completedCount + 1,
          }))
          break
      }
    }

    room.on(RoomEvent.DataReceived, handleDataReceived)
    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room])

  return state
}
